import { useMemo } from "react";
import isEqual from "lodash/isEqual";

import {
  PermitClassification,
  PermitFilters,
  PermitStatus,
  PermitRootType,
  PermitSubType,
} from "@landtechnologies/search-layer-contracts";
import { useLocalStorageReducer } from "react-migration/lib/persistence/state/useLocalStorageReducer";

export type PermitTypeId = PermitRootType | `${PermitRootType}.${PermitSubType}`;

export type PermitFormState = Omit<PermitFilters, "enabledTypes" | "enabledSubTypes"> & {
  enabledTypes: PermitTypeId[];
};

export const defaultPermitFilterState: PermitFormState = {
  enabledClassifications: ["Residential", "Commercial", "Other"],
  enabledStatuses: ["Approved", "Pending", "Withdrawn", "Rejected", "Uncategorized"],
  enabledTypes: [
    "Conceptual",
    "Construction",
    "Construction.Notice",
    "Construction.Individual",
    "Construction.General",
    "Modification",
    "Modification.Exempt",
    "Modification.Modification",
    "Uncategorized",
  ],
  maxAge: 5,
  projectSize: [null, null],
  freeTextSearch: "",
};

type Action =
  | { type: "SET_ENABLED_CLASSIFICATIONS"; value: PermitClassification[] }
  | { type: "SET_ENABLED_TYPES"; value: PermitTypeId[] }
  | { type: "SET_ENABLED_STATUSES"; value: PermitStatus[] }
  | { type: "SET_FREETEXT_FILTER"; value: string }
  | { type: "SET_PROJECT_SIZE_MIN"; value: number | null }
  | { type: "SET_PROJECT_SIZE_MAX"; value: number | null }
  | { type: "SET_SUBMISSION_MAX_AGE"; value: number | undefined }
  | { type: "RESET_STATE" };

function createReducer(initialState: PermitFormState) {
  return function reducer(state: PermitFormState, action: Action): PermitFormState {
    switch (action.type) {
      case "SET_ENABLED_CLASSIFICATIONS":
        return { ...state, enabledClassifications: action.value };
      case "SET_ENABLED_TYPES":
        return { ...state, enabledTypes: action.value };
      case "SET_ENABLED_STATUSES":
        return { ...state, enabledStatuses: action.value };
      case "SET_PROJECT_SIZE_MIN":
        return { ...state, projectSize: [action.value, state.projectSize[1]] };
      case "SET_PROJECT_SIZE_MAX":
        return { ...state, projectSize: [state.projectSize[0], action.value] };
      case "SET_SUBMISSION_MAX_AGE":
        return { ...state, maxAge: action.value };
      case "SET_FREETEXT_FILTER":
        return { ...state, freeTextSearch: action.value };
      case "RESET_STATE":
        return { ...initialState };
    }
  };
}

export type PermitState = ReturnType<typeof usePermitFilterState>[0];
export type PermitStateDispatch = ReturnType<typeof usePermitFilterState>[1];

export function usePermitFilterState() {
  const [state, dispatch] = useLocalStorageReducer(
    "permits",
    createReducer(defaultPermitFilterState),
    defaultPermitFilterState
  );

  const hasFilters = useMemo(() => !isEqual(defaultPermitFilterState, state), [state]);

  return [state, dispatch, { hasFilters }] as const;
}
